import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, IconButton, Input, Segmented, Select, TextArea, useToast, type Tone } from "@agentsaas/ui";
import { McpServer, Secret, type MediaProviderKey } from "@agentsaas/protocol";
import type { CallOutput } from "@agentsaas/client-sdk";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { formatDateTime } from "../app/format";
import { Loading } from "../layout/Loading";
import { capk, useMcpServers, useMediaKeys, useMediaProviders, useSecrets } from "../data/capabilities";
import { CapFrame } from "./common";
import { SecretRefInput } from "./SecretRefInput";

/**
 * 租户设置里的集成（/t/:tid/integrations/{secrets,mcp,media}）：
 * - 密钥：列表（只有名字、说明与 hint）、新建或覆盖（值只写不读）、删除（被引用时服务端拒绝）
 * - 外部 MCP 服务：登记（URL + 请求头模板，值里 {{secret:NAME}} 可补全）、探测显示工具、删除
 * - 媒体供应商：平台是否提供 key；绑定 / 更换 / 解绑自带 key（用自带 key 时不扣平台钱包）
 */

const SECRET_EXAMPLE = "{{secret:NAME}}";
const REF_RE = /\{\{\s*secret:([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
const refsIn = (s: string) => [...s.matchAll(REF_RE)].map((m) => m[1]!);

function ConfirmDialog({ open, title, body, confirm, onClose, onConfirm, testId }: { open: boolean; title: string; body: string; confirm: string; onClose(): void; onConfirm(): void; testId?: string }) {
  const { t } = useT();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      closeLabel={t("shell.close")}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="danger" onClick={onConfirm} data-testid={testId}>
            {confirm}
          </Button>
        </>
      }
    >
      <p>{body}</p>
    </Dialog>
  );
}

// ───────────── 密钥 ─────────────

export function SecretsPage() {
  const { tid = "" } = useParams();
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const secrets = useSecrets(tid);
  const [editing, setEditing] = useState<{ name: string | null } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const del = async (name: string) => {
    setDeleting(null);
    try {
      await api.call("secretDelete", { params: { tid, name } });
      toast.show({ message: t("integ.secret.deleted", { name }), tone: "success" });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      void qc.invalidateQueries({ queryKey: capk.secrets(tid) });
    }
  };
  return (
    <CapFrame section="secrets">
      <div className="stack">
        <div className="page__head">
          <h1 className="page__title">{t("cap.nav.secrets")}</h1>
          <Button variant="primary" icon="plus" onClick={() => setEditing({ name: null })} data-testid="secret-new">
            {t("integ.secret.new")}
          </Button>
        </div>
        <p className="muted small">{t("integ.secret.intro", { example: SECRET_EXAMPLE })}</p>
        {secrets.isPending ? (
          <Loading />
        ) : secrets.data?.length ? (
          <ul className="integ-list" data-testid="secret-list">
            {secrets.data.map((s) => (
              <li key={s.id} className="card integ-item" data-testid="secret-item" data-name={s.name}>
                <div className="integ-item__head">
                  <code className="grow">
                    <strong>{s.name}</strong>
                  </code>
                  {s.hint ? <Badge>{s.hint}</Badge> : null}
                  <Button size="sm" variant="secondary" icon="edit" onClick={() => setEditing({ name: s.name })} data-testid="secret-overwrite">
                    {t("integ.secret.overwrite")}
                  </Button>
                  <IconButton icon="trash" label={t("integ.secret.delete", { name: s.name })} onClick={() => setDeleting(s.name)} data-testid="secret-delete" />
                </div>
                {s.description ? <p className="small">{s.description}</p> : null}
                <span className="muted small">{t("integ.updated_at", { time: formatDateTime(s.updatedAt, locale) })}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="key" title={t("integ.secret.empty")} />
        )}
      </div>
      <SecretDialog
        tid={tid}
        open={!!editing}
        fixedName={editing?.name ?? null}
        current={secrets.data?.find((s) => s.name === editing?.name)}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void qc.invalidateQueries({ queryKey: capk.secrets(tid) });
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        title={t("integ.secret.delete", { name: deleting ?? "" })}
        body={t("integ.secret.delete_confirm")}
        confirm={t("integ.delete")}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && void del(deleting)}
        testId="secret-delete-confirm"
      />
    </CapFrame>
  );
}

/** 新建或覆盖密钥；fixedName 非空 = 覆盖（名字不可改） */
export function SecretDialog({
  tid,
  open,
  fixedName,
  current,
  onClose,
  onSaved,
}: {
  tid: string;
  open: boolean;
  fixedName: string | null;
  current?: Secret;
  onClose(): void;
  onSaved(s: Secret): void;
}) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const n = fixedName ?? name.trim();
  const nameOk = Secret.shape.name.safeParse(n).success;
  const close = () => {
    setName("");
    setValue("");
    setDescription("");
    setError(null);
    setTouched(false);
    onClose();
  };
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (!nameOk || !value) return;
    setBusy(true);
    setError(null);
    try {
      const s = await api.call("secretPut", { params: { tid, name: n }, body: { value, ...(description.trim() || !fixedName ? { description: description.trim() } : {}) } });
      toast.show({ message: fixedName ? t("integ.secret.overwritten", { name: n }) : t("integ.secret.created", { name: n }), tone: "success" });
      setName("");
      setValue("");
      setDescription("");
      setTouched(false);
      onSaved(s);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={fixedName ? t("integ.secret.overwrite_title", { name: fixedName }) : t("integ.secret.new")}
      closeLabel={t("shell.close")}
      size="sm"
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} data-testid="secret-save">
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        {fixedName ? null : (
          <Field label={t("integ.secret.name")} hint={t("integ.secret.name_hint")} error={touched && !nameOk ? t("integ.secret.name_invalid") : undefined}>
            {(id, desc) => <Input id={id} aria-describedby={desc} className="mono" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" data-testid="secret-name" />}
          </Field>
        )}
        <Field label={t("integ.secret.value")} hint={t("integ.secret.value_hint")} error={touched && !value ? t("integ.secret.value_required") : undefined}>
          {(id, desc) => <Input id={id} aria-describedby={desc} type="password" value={value} onChange={(e) => setValue(e.target.value)} autoComplete="new-password" data-testid="secret-value" />}
        </Field>
        <Field label={t("integ.secret.description")}>
          {(id) => <TextArea id={id} rows={2} value={description} placeholder={current?.description} onChange={(e) => setDescription(e.target.value)} data-testid="secret-desc" />}
        </Field>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

// ───────────── 外部 MCP 服务 ─────────────

const STATUS_TONE: Record<McpServer["status"], Tone> = { unverified: "warning", ok: "success", error: "danger" };

export function McpServersPage() {
  const { tid = "" } = useParams();
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const servers = useMcpServers(tid);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<McpServer | null>(null);
  const [probing, setProbing] = useState<Set<string>>(new Set());
  const refresh = () => qc.invalidateQueries({ queryKey: capk.servers(tid) });
  const probe = async (s: Pick<McpServer, "id" | "name">) => {
    setProbing((x) => new Set(x).add(s.id));
    try {
      const out = await api.call("mcpServerProbe", { params: { msid: s.id } });
      qc.setQueryData<McpServer[]>(capk.servers(tid), (xs) => xs?.map((x) => (x.id === out.id ? out : x)));
      toast.show({ message: t("integ.mcp.probe_ok", { name: s.name, count: out.tools.length }), tone: "success" });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      void refresh();
    } finally {
      setProbing((x) => {
        const n = new Set(x);
        n.delete(s.id);
        return n;
      });
    }
  };
  const del = async (s: McpServer) => {
    setDeleting(null);
    try {
      await api.call("mcpServerDelete", { params: { msid: s.id } });
      toast.show({ message: t("integ.mcp.deleted", { name: s.name }), tone: "success" });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      void refresh();
    }
  };
  return (
    <CapFrame section="mcp">
      <div className="stack">
        <div className="page__head">
          <h1 className="page__title">{t("cap.nav.mcp")}</h1>
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)} data-testid="mcp-new">
            {t("integ.mcp.new")}
          </Button>
        </div>
        <p className="muted small">{t("integ.mcp.intro")}</p>
        {servers.isPending ? (
          <Loading />
        ) : servers.data?.length ? (
          <ul className="integ-list" data-testid="mcp-list">
            {servers.data.map((s) => (
              <li key={s.id} className="card integ-item" data-testid="mcp-item" data-name={s.name} data-status={s.status}>
                <div className="integ-item__head">
                  <strong className="grow ellipsis">{s.name}</strong>
                  <Badge tone={STATUS_TONE[s.status]}>{t(`integ.mcp.status.${s.status}`)}</Badge>
                  <Button size="sm" variant="secondary" icon="refresh" loading={probing.has(s.id)} onClick={() => void probe(s)} data-testid="mcp-probe">
                    {t("integ.mcp.probe")}
                  </Button>
                  <IconButton icon="trash" label={t("integ.mcp.delete", { name: s.name })} onClick={() => setDeleting(s)} data-testid="mcp-delete" />
                </div>
                <code className="small ellipsis">{s.url}</code>
                {Object.keys(s.headers).length ? (
                  <dl className="kv small">
                    {Object.entries(s.headers).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt>
                          <code>{k}</code>
                        </dt>
                        <dd>
                          <code>{v}</code>
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
                {s.status === "error" && s.lastError ? (
                  <p className="form-error small" data-testid="mcp-error">
                    {s.lastError}
                  </p>
                ) : null}
                {s.tools.length ? (
                  <details open data-testid="mcp-tools">
                    <summary className="small">{t("integ.mcp.tools", { count: s.tools.length })}</summary>
                    <ul className="integ-tools">
                      {s.tools.map((tool) => (
                        <li key={tool.name} data-testid="mcp-tool">
                          <code>{tool.name}</code>
                          {tool.description ? <span className="muted"> {tool.description}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : s.status === "unverified" ? (
                  <p className="muted small">{t("integ.mcp.unverified_hint")}</p>
                ) : null}
                <span className="muted small">{t("integ.created_at", { time: formatDateTime(s.createdAt, locale) })}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="plug" title={t("integ.mcp.empty")} />
        )}
      </div>
      <McpServerDialog
        tid={tid}
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(s) => {
          setAdding(false);
          qc.setQueryData<McpServer[]>(capk.servers(tid), (xs) => (xs ? [...xs, s] : xs));
          void probe(s);
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        title={t("integ.mcp.delete", { name: deleting?.name ?? "" })}
        body={t("integ.mcp.delete_confirm")}
        confirm={t("integ.delete")}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && void del(deleting)}
        testId="mcp-delete-confirm"
      />
    </CapFrame>
  );
}

interface HeaderRow {
  key: string;
  value: string;
}

function McpServerDialog({ tid, open, onClose, onCreated }: { tid: string; open: boolean; onClose(): void; onCreated(s: McpServer): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const secrets = useSecrets(open ? tid : "");
  const names = (secrets.data ?? []).map((s) => s.name);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [headers, setHeaders] = useState<HeaderRow[]>([{ key: "Authorization", value: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const urlOk = McpServer.shape.url.safeParse(url.trim()).success && /^https?:/i.test(url.trim());
  const nameOk = !!name.trim();
  const rows = headers.filter((h) => h.key.trim() || h.value.trim());
  const keyOk = rows.every((h) => /^[A-Za-z0-9-]+$/.test(h.key.trim()));
  const unknown = [...new Set(rows.flatMap((h) => refsIn(h.value)).filter((n) => !names.includes(n)))];
  const reset = () => {
    setName("");
    setUrl("");
    setHeaders([{ key: "Authorization", value: "" }]);
    setError(null);
    setTouched(false);
  };
  const close = () => {
    reset();
    onClose();
  };
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (!nameOk || !urlOk || !keyOk || unknown.length) return;
    setBusy(true);
    setError(null);
    try {
      const s = await api.call("mcpServerCreate", { params: { tid }, body: { name: name.trim(), url: url.trim(), headers: Object.fromEntries(rows.map((h) => [h.key.trim(), h.value.trim()])) } });
      reset();
      onCreated(s);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={t("integ.mcp.new")}
      closeLabel={t("shell.close")}
      size="md"
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} data-testid="mcp-save">
            {t("integ.mcp.register")}
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        <Field label={t("integ.mcp.name")} error={touched && !nameOk ? t("integ.mcp.name_required") : undefined}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={64} data-testid="mcp-name" />}
        </Field>
        <Field label={t("integ.mcp.url")} hint={t("integ.mcp.url_hint")} error={touched && !urlOk ? t("integ.mcp.url_invalid") : undefined}>
          {(id, desc) => <Input id={id} aria-describedby={desc} type="url" inputMode="url" className="mono" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="mcp-url" />}
        </Field>
        <fieldset className="stack">
          <legend className="ui-field__label">{t("integ.mcp.headers")}</legend>
          <p className="ui-field__hint">{t("integ.mcp.headers_hint", { example: SECRET_EXAMPLE })}</p>
          {headers.map((h, i) => {
            const upd = (patch: Partial<HeaderRow>) => setHeaders((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            return (
              <div key={i} className="headers-row">
                <Input aria-label={t("integ.mcp.header_name", { n: i + 1 })} className="mono" value={h.key} onChange={(e) => upd({ key: e.target.value })} data-testid="mcp-header-key" />
                <SecretRefInput
                  value={h.value}
                  onChange={(v) => upd({ value: v })}
                  secrets={names}
                  label={t("integ.mcp.header_value", { n: i + 1 })}
                  listLabel={t("integ.mcp.secret_suggestions")}
                  testId="mcp-header-value"
                />
                <IconButton icon="trash" label={t("integ.mcp.header_remove", { n: i + 1 })} onClick={() => setHeaders((xs) => xs.filter((_, j) => j !== i))} />
              </div>
            );
          })}
          <div className="row">
            <Button size="sm" icon="plus" onClick={() => setHeaders((xs) => [...xs, { key: "", value: "" }])} data-testid="mcp-header-add">
              {t("integ.mcp.header_add")}
            </Button>
            {!names.length ? (
              <span className="muted small">
                {t("integ.mcp.no_secrets")} <Link to={`/t/${tid}/integrations/secrets`}>{t("cap.nav.secrets")}</Link>
              </span>
            ) : null}
          </div>
          {touched && !keyOk ? <p className="form-error small">{t("integ.mcp.header_key_invalid")}</p> : null}
          {unknown.length ? (
            <p className="form-error small" role="alert" data-testid="mcp-unknown-secret">
              {t("integ.mcp.unknown_secret", { names: unknown.join(", ") })}
            </p>
          ) : null}
        </fieldset>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

// ───────────── 媒体供应商 ─────────────

type Provider = CallOutput<"mediaProviderList">["items"][number];

export function MediaProvidersPage() {
  const { tid = "" } = useParams();
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const providers = useMediaProviders();
  const keys = useMediaKeys(tid);
  const secrets = useSecrets(tid);
  const [binding, setBinding] = useState<Provider | null>(null);
  const [unbinding, setUnbinding] = useState<Provider | null>(null);
  const unbind = async (p: Provider) => {
    setUnbinding(null);
    try {
      await api.call("mediaKeyDelete", { params: { tid, provider: p.id } });
      toast.show({ message: t("integ.media.unbound", { provider: p.id }), tone: "success" });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      void qc.invalidateQueries({ queryKey: capk.mediaKeys(tid) });
    }
  };
  return (
    <CapFrame section="media">
      <div className="stack">
        <div className="page__head">
          <h1 className="page__title">{t("cap.nav.media")}</h1>
        </div>
        <p className="banner small" data-testid="media-billing-note">
          {t("integ.media.intro")}
        </p>
        {providers.isPending || keys.isPending ? (
          <Loading />
        ) : providers.data?.length ? (
          <ul className="integ-list" data-testid="media-list">
            {providers.data.map((p) => {
              const k = keys.data?.find((x) => x.provider === p.id);
              const secret = k ? secrets.data?.find((s) => s.id === k.secretId) : undefined;
              const own = !!k && k.enabled;
              return (
                <li key={p.id} className="card integ-item" data-testid="media-item" data-provider={p.id}>
                  <div className="integ-item__head">
                    <strong className="grow">
                      <code>{p.id}</code>
                    </strong>
                    {p.kinds.map((kind) => (
                      <Badge key={kind}>{t(`integ.media.kind.${kind}`)}</Badge>
                    ))}
                    <Badge tone={p.platformKey ? "success" : "neutral"}>{p.platformKey ? t("integ.media.platform_yes") : t("integ.media.platform_no")}</Badge>
                  </div>
                  <p className="small" data-testid="media-source">
                    {own ? (
                      <>
                        <Badge tone="accent">{t("integ.media.using_own")}</Badge> <span>{t("integ.media.own_secret", { name: secret?.name ?? k.secretId })}</span>
                      </>
                    ) : k ? (
                      <span className="muted">{t("integ.media.own_disabled", { name: secret?.name ?? k.secretId })}</span>
                    ) : p.platformKey ? (
                      <span className="muted">{t("integ.media.using_platform")}</span>
                    ) : (
                      <span className="text-danger">{t("integ.media.unavailable")}</span>
                    )}
                  </p>
                  <div className="row">
                    <Button size="sm" variant={k ? "secondary" : "primary"} icon="key" onClick={() => setBinding(p)} data-testid="media-bind">
                      {k ? t("integ.media.change") : t("integ.media.bind")}
                    </Button>
                    {k ? (
                      <Button size="sm" variant="ghost" icon="trash" onClick={() => setUnbinding(p)} data-testid="media-unbind">
                        {t("integ.media.unbind")}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState icon="image" title={t("integ.media.empty")} />
        )}
      </div>
      <MediaKeyDialog
        tid={tid}
        provider={binding}
        current={binding ? keys.data?.find((x) => x.provider === binding.id) : undefined}
        onClose={() => setBinding(null)}
        onSaved={() => {
          setBinding(null);
          void qc.invalidateQueries({ queryKey: capk.mediaKeys(tid) });
          void qc.invalidateQueries({ queryKey: capk.secrets(tid) });
        }}
      />
      <ConfirmDialog
        open={!!unbinding}
        title={t("integ.media.unbind")}
        body={unbinding?.platformKey ? t("integ.media.unbind_confirm") : t("integ.media.unbind_confirm_noplatform")}
        confirm={t("integ.media.unbind")}
        onClose={() => setUnbinding(null)}
        onConfirm={() => unbinding && void unbind(unbinding)}
        testId="media-unbind-confirm"
      />
    </CapFrame>
  );
}

type KeySource = "existing" | "new";

function MediaKeyDialog({ tid, provider, current, onClose, onSaved }: { tid: string; provider: Provider | null; current?: MediaProviderKey; onClose(): void; onSaved(): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const secrets = useSecrets(provider ? tid : "");
  const list = secrets.data ?? [];
  const [source, setSource] = useState<KeySource | null>(null);
  const [secretId, setSecretId] = useState<string>("");
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const src: KeySource = source ?? (list.length ? "existing" : "new");
  const sel = secretId || current?.secretId || list[0]?.id || "";
  const on = enabled ?? current?.enabled ?? true;
  const suggested = provider ? `${provider.id.toUpperCase().replace(/[^A-Z0-9]+/g, "_")}_KEY` : "";
  const nm = newName || suggested;
  const newOk = Secret.shape.name.safeParse(nm).success && !!newValue;
  const reset = () => {
    setSource(null);
    setSecretId("");
    setNewName("");
    setNewValue("");
    setEnabled(null);
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!provider) return;
    if (src === "new" ? !newOk : !sel) return;
    setBusy(true);
    setError(null);
    try {
      const sid = src === "new" ? (await api.call("secretPut", { params: { tid, name: nm }, body: { value: newValue, description: t("integ.media.secret_desc", { provider: provider.id }) } })).id : sel;
      await api.call("mediaKeyPut", { params: { tid, provider: provider.id }, body: { secretId: sid as MediaProviderKey["secretId"], enabled: on } });
      toast.show({ message: on ? t("integ.media.bound", { provider: provider.id }) : t("integ.media.saved_disabled", { provider: provider.id }), tone: "success" });
      reset();
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={!!provider}
      onClose={close}
      title={t("integ.media.bind_title", { provider: provider?.id ?? "" })}
      closeLabel={t("shell.close")}
      size="sm"
      footer={
        <>
          <Button onClick={close}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={src === "new" ? !newOk : !sel} onClick={() => void submit()} data-testid="media-save">
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        <p className="banner small">{t("integ.media.no_charge")}</p>
        {list.length ? (
          <Segmented<KeySource>
            label={t("integ.media.source")}
            value={src}
            onChange={setSource}
            options={[
              { value: "existing", label: t("integ.media.use_existing") },
              { value: "new", label: t("integ.media.create_secret") },
            ]}
          />
        ) : null}
        {src === "existing" ? (
          <Field label={t("integ.media.secret")}>
            {(id) => (
              <Select id={id} value={sel} onChange={(e) => setSecretId(e.target.value)} data-testid="media-secret">
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.hint ? ` (${s.hint})` : ""}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <>
            <Field label={t("integ.secret.name")} hint={t("integ.secret.name_hint")}>
              {(id, desc) => <Input id={id} aria-describedby={desc} className="mono" value={nm} onChange={(e) => setNewName(e.target.value)} data-testid="media-new-name" />}
            </Field>
            <Field label={t("integ.media.key_value", { provider: provider?.id ?? "" })} hint={t("integ.secret.value_hint")}>
              {(id, desc) => <Input id={id} aria-describedby={desc} type="password" autoComplete="new-password" value={newValue} onChange={(e) => setNewValue(e.target.value)} data-testid="media-new-value" />}
            </Field>
          </>
        )}
        <label className="check">
          <input type="checkbox" checked={on} onChange={(e) => setEnabled(e.target.checked)} data-testid="media-enabled" />
          <span>{t("integ.media.enabled")}</span>
        </label>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
